<script setup>
import { ref, computed, onMounted, useTemplateRef, watch, provide, readonly } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex'
import ModalBox from './ModalBox.vue'
import StreamsWidget from '../ui/StreamsWidget.vue'
import FormButton from '../Atoms/FormButton.vue'
import { useMatchMedia } from '@/utils'
import { selectionState, applyBulk } from '@/Utils/playlistBulkSelection'

const store = useStore()

//Emits
const emit = defineEmits({
	'add-stream': () => true
})

// TemplateRefs
const streamsWidget = useTemplateRef('streamsWidget')

// CONST
const isDesktop = useMatchMedia('(min-width: 768px)')

// Models
const isVisible = defineModel('isVisible')

// Working copy of playlist streams, synced on open
const playlistItems = ref([])
const initialPlaylistItems = ref([])
// Bumped on every open so async work started in an earlier open can be dropped
const selectionSession = ref(0)

// Computed
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const currentPlaylist = computed(() => store.getters['Playlist/currentPlaylist'])

const addedCount = computed(() => {
	const initialIds = new Set(initialPlaylistItems.value.map(s => s.id))
	return playlistItems.value.filter(s => !initialIds.has(s.id)).length
})

const removedCount = computed(() => {
	const currentIds = new Set(playlistItems.value.map(s => s.id))
	return initialPlaylistItems.value.filter(s => !currentIds.has(s.id)).length
})

const currentStreams = computed(() => {
	const parts = []
	if (currentPlaylist.value?.streams?.filter(s => s.type === 'live').length > 0) {
		parts.push(`${currentPlaylist.value?.streams?.filter(s => s.type === 'live').length} Livestream${currentPlaylist.value?.streams?.filter(s => s.type === 'live').length > 1 ? 's' : ''}`)
	}
	if (currentPlaylist.value?.streams?.filter(s => s.type === 'vod').length > 0) {
		parts.push(`${currentPlaylist.value?.streams?.filter(s => s.type === 'vod').length} VOD${currentPlaylist.value?.streams?.filter(s => s.type === 'vod').length > 1 ? 's' : ''}`)
	}
	return parts.length > 0 ? 'Current Playlist: ' + parts.join(', ') : ''
})	
const computedLabel = computed(() => {
	const parts = []
	if (addedCount.value > 0) parts.push(`${addedCount.value} item${addedCount.value > 1 ? 's' : ''} will be added`)
	if (removedCount.value > 0) parts.push(`${removedCount.value} item${removedCount.value > 1 ? 's' : ''} will be removed`)
	return parts.length > 0 ? 'Changes: ' + parts.join(', ') : ''
})

const isDisabled = computed(() => addedCount.value === 0 && removedCount.value === 0)

const getPlaylistEntryId = (stream, type) => {
	if (type === 'livestream') return stream._id
	if (stream.refId) return stream.refId
	return stream.id
}

const toPlaylistEntry = (stream, type) => {
	let streamId = ''
	let streamName = ''
	let streamType = ''
	let isLivetovod = false
	if (type === 'livestream') {
		streamId = stream._id
		streamName = stream.name
		streamType = 'live'
	} else {
		streamId = stream.id
		streamName = stream.fileName
		streamType = 'vod'
	}
	if (stream.refId) {
		streamId = stream.refId
		isLivetovod = true
	}
	return {
		id: streamId,
		type: streamType,
		isLiveToVod: isLivetovod,
		name: streamName,
	}
}

const syncPlaylistItems = () => {
	const streams = _.cloneDeep(currentPlaylist.value?.streams ?? [])
	playlistItems.value = streams
	initialPlaylistItems.value = _.cloneDeep(streams)
	selectionSession.value++
}

const selectedIds = computed(() => new Set(playlistItems.value.map(el => el.id)))

const isSelected = (stream, type) => selectedIds.value.has(getPlaylistEntryId(stream, type))

const getSelectionState = (streams, type) =>
	selectionState(streams.map(stream => getPlaylistEntryId(stream, type)), selectedIds.value)

const setSelected = (streams, type, select) => {
	const entries = streams.map(stream => toPlaylistEntry(stream, type))
	playlistItems.value = applyBulk(playlistItems.value, entries, select)
}

const togglePlaylistItem = (stream, type) => {
	const id = getPlaylistEntryId(stream, type)
	const idx = playlistItems.value.findIndex(el => el.id === id)
	if (idx >= 0) {
		playlistItems.value.splice(idx, 1)
	} else {
		playlistItems.value.unshift(toPlaylistEntry(stream, type))
	}
}

provide('playlistContentSelection', {
	isSelected,
	toggle: togglePlaylistItem,
	setSelected,
	getState: getSelectionState,
	session: readonly(selectionSession),
	// Saved VODs don't record their folder, so the VOD tab loads folder contents
	// up front only when there is saved VOD selection to reflect
	hasSavedVods: computed(() => initialPlaylistItems.value.some(s => s.type === 'vod')),
})

// Hooks
onMounted(async () => {
	if (vodStreams.value.length === 0 || allInOneStreams.value.length === 0) {
		await store.dispatch('Streams/getStreams')
	}
})

// Methods
const saveChanges = () => {
	emit('add-stream', _.cloneDeep(playlistItems.value))
	isVisible.value = false
}

const onChange = (stream, type) => {
	togglePlaylistItem(stream, type)
}

watch(isVisible, (visible) => {
	if (visible) {
		syncPlaylistItems()
	}
	if (!isDesktop.value && streamsWidget.value) {
		if (visible) {
			streamsWidget.value.openView()
		} else {
			streamsWidget.value.closeView({ emitEvent: false })
		}
	}
})
</script>

<template>
	<StreamsWidget
		ref="streamsWidget"
		v-if="!isDesktop"
		placement="body"
		source="playlist"
		hide-notification
		@add-stream="onChange"
		@close="isVisible = false"
		header-title="Select video source"
		:show-plus-button="false"
		tab-size="1/2"
		variant="modern"
	>
		<template #footer>
			<p class="text-tiny leading-tight text-surface-8 !mt-3 mb-0">{{ currentStreams }}</p>
			<p class="text-tiny leading-tight text-surface-8 mb-0">{{ computedLabel }}</p>
			<div class="flex flex-col gap-2 !my-3">
				<FormButton class="justify-center" full-width label="Close" type="secondary" @click="isVisible = false" />
				<FormButton class="justify-center" full-width label="Save changes" @click="saveChanges" :disabled="isDisabled" />
			</div>
		</template>
	</StreamsWidget>
	<ModalBox v-else v-model="isVisible" size="md" title="Select video source" wrapper-classes="lg:min-h-[660px]">
		<div class="px-6 pb-6">
			<StreamsWidget v-if="isDesktop" placement="body" source="playlist" variant="modern" 
				@add-stream="onChange" tab-size="1/2" />
		</div>
		<template #footer>
			<div class="px-6 pb-6 flex items-center justify-end gap-x-2">
				<div class="flex flex-col gap-x-2 mr-auto">
					<span class="text-tiny leading-tight text-surface-8">{{ currentStreams }}</span>
					<span class="text-tiny leading-tight text-surface-8">{{ computedLabel }}</span>
				</div>
				<FormButton label="Close" type="secondary" @click="isVisible = false" />
				<FormButton label="Confirm" @click="saveChanges" :disabled="isDisabled" />
			</div>
		</template>
	</ModalBox>
</template>

<style></style>
